import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { CustomerApis } from 'apis/CustomerApis';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { GET_ALL_PLANT } from 'apis/GraphQL/Queries/plantQuery';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import Content from 'layouts/Content';
import React, { useCallback, useEffect, useState } from 'react';

interface IProps {
  isOpen: boolean;
  defaultFilter: any;
  onCancel: () => void;
  onFinish: (values: any) => void;
}

const spanCol = 6;

const FilterPaymentInfo = (props: IProps) => {
  const { isOpen, defaultFilter, onCancel, onFinish } = props;
  const { user } = useUser();
  const [form] = useForm();

  const [optionsBranch, setOptionsBranch] = useState<DefaultOptionType[]>([]);
  const [customerData, setCustomersData] =
    useState<ILazySelectData>(initLazySelectData);

  const mutationBranch = useMutation({
    mutationKey: ['getAllBranchReport'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsBranch(
        res.data?.getPlant
          ? res.data?.getPlant.items.map((item: any) => ({
              label: item.id + ' - ' + item.name,
              value: item.id,
            }))
          : [],
      );
    },
  });

  const { mutate: getCustomersMutate, isPending: isGetCustomersPending } =
    useMutation({
      mutationFn: (params: {
        pageSize: number;
        searchValue: string;
        plantId?: string;
      }) => CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomer) {
          const data = res?.data?.getCustomer;
          setCustomersData({
            options: data.items.map(
              (item: any) =>
                ({
                  label:
                    (item?.sapCustomerId ?? 'DMS') +
                    (item?.storeName ? ` - ${item.storeName}` : ''),
                  value: item?.id,
                }) as DefaultOptionType,
            ),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
    });

  useEffect(() => {
    if (user?.isAdmin || user?.isHO) {
      mutationBranch.mutate({
        query: GET_ALL_PLANT,
        variables: {
          input: {
            id: {
              not: '5000',
            },
          },
        },
      });
    }
  }, [user]);

  useEffect(() => {
    form.setFieldsValue(defaultFilter);
  }, [defaultFilter, form]);

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(defaultFilter);
    onCancel();
  };

  const handleCustomerSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;
      getCustomersMutate({
        pageSize,
        searchValue: value || '',
      });
    },
    [getCustomersMutate],
  );

  return (
    <Content isOpen={isOpen}>
      <FormFilter form={form} onFinish={onFinish} onCancel={handleCancel}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                options={optionsBranch}
                disabled={!(user?.isAdmin || user?.isHO)}
                placeholder="Mã chi nhánh"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã THTT" name="id">
              <InputForm placeholder="Nhập mã THTT" />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã bảng kê" name="dmsOrderCode">
              <InputForm placeholder="Nhập mã bảng kê / Thu dẻ" />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="BILL" name="billingCode">
              <InputForm placeholder="Nhập mã BILL / Billing" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-4">
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã KH" name="customerId">
              <LazySelectForm
                data={customerData}
                loading={isGetCustomersPending}
                placeholder="Tìm MST/Mã khách hàng"
                onSelectScrollChange={handleCustomerSelectScrollChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Ngày tạo từ - đến"
              name="fromDateToDate"
              rules={[{ required: true, message: 'Vui lòng chọn ngày tạo' }]}
            >
              <DatePicker.RangePicker
                placeholder={['Từ ngày', 'Đến ngày']}
                format={'DD/MM/YYYY'}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="SO/PO" name="soPo">
              <InputForm placeholder="Nhập mã SO/PO" />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Loại SO" name="orderType">
              <SelectForm
                placeholder="Chọn loại SO"
                options={[
                  { label: 'ZW2', value: 'ZW2' },
                  { label: 'ZWY', value: 'ZWY' },
                  { label: 'RE', value: 'RE' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="mt-4">
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã giao dịch" name="transactionCode">
              <InputForm placeholder="Nhập mã giao dịch" />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Thời gian thanh toán từ - đến"
              name="fromPaymentDateToPaymentDate"
            >
              <DatePicker.RangePicker
                placeholder={['Từ ngày', 'Đến ngày']}
                format={'DD/MM/YYYY'}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterPaymentInfo;
